import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { appStorage, auth } from "../../../firebase";
import { pdf } from '@react-pdf/renderer';
import QRCode from "qrcode";
import { ref, getBlob, uploadBytes } from "firebase/storage";
import { Spiner } from "../../../components/spinner/Spiner";
import TableList, { GridCol, GridRow } from "../../../components/table/TableList"
import Button, {BtnProps} from "../../../components/button/Button"
import { ModalAndAlmightyDialog } from "../../../components/modal/ModalAndAlmightyDialog";
import { AlertValue, alertOpen } from "../../../features/alert.slice";
import authApi from "../../../api/Auth.api";
import userQuery from "../../../firestore/User.query";
import companiesQuery from "../../../firestore/Companies.query";
import systemParametersQuery from "../../../firestore/SystemParameters.query";
import { Company } from "../../../model/Companies";
import { User } from "../../../model/Users";
import { UserCardCreateState, userCardTimestampToDate } from "../../../model/Systemparameters";
import ExcelJS from "exceljs";
import { Progress } from "../../../components/progress/Progress";
import { ConstMessage } from "../../../config/const";
import { convertDateTimeToString, convertDateTimeToJapaneseString } from "../../../utility/ConvertUtily";
import { UserIdCardErrorInfoModal } from "./UserIdCardErrorInfoModal";
import { UserIdCardPDF } from "./UserIdCardPDF";
import { DocumentArrowUpIcon } from "@heroicons/react/24/solid";
import { SystemSetting } from "../../../model/SystemSetting";
import companiesApi from "../../../api/Companies.api"
import { checkOption } from "../../../controller/OptionController";

export const UserIdCardCreatingPage = () => {

  // URLパラメータからtenantId取得
  const { tenantId } = useParams<{ tenantId: string }>();
  const currentUser = auth.currentUser;

  const dispatch = useDispatch();

  const [isLoad, setIsLoad] = useState(true);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [isOpenErrorInfoDialog, setIsOpenErrorInfoDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [dialogChildren, setDialogChildren] = useState<React.ReactNode | undefined>();
  const [dialogActions, setDialogActions] = useState<BtnProps[]>([]);
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [company, setCompany] = useState<Company>();
  const [userIdCardList, setUserIdCardList] = useState<UserCardCreateState[]>([]);
  const [progressSubText, setProgressSubText] = useState<string>('');
  const [isPdfCreate, setIsPdfCreate] = useState(false);
  const [pdfFlg, setPdfFlg] = useState(false);
  const [userIdCardErrorMessages, setUserIdCardErrorMessages] = useState<{userId: string, userName: string, message: string[]}[]>([]);
  const [usersList, setUsersList] = useState<string[]>([]);
  const [errorUserList, setErrorUserList] = useState<User[]>([]);
  const [systemSetting, setSystemSetting] = useState<SystemSetting>();
  const [machitappuUrl, setMachitappuUrl] = useState('');
  const [dispFlg, setDispFlg] = useState(false);
  
  // 初期表示
  useEffect(() => {
    const f = async () => {
      try{
        const displayFlg = await displayStatus();
        setDispFlg(displayFlg);
        if(displayFlg) {
          const company = await companiesQuery.getCompany(tenantId!);
          setCompany(company);
          getUserIdCardCreatList();
          const systemSetting = await companiesQuery.getSystemSetting(tenantId!);
          setSystemSetting(systemSetting);
          const machitappuUrl = await systemParametersQuery.getMachitappuURL() as string;
          setMachitappuUrl(machitappuUrl);
        }
      }catch(err: any){
        console.log(err);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_DATA, "danger")));
      }
      setIsLoad(false);
    };
    f();
  }, []);

  // PDF出力用
  useEffect(() => {
    if(loadProgress === 100) {
      // PDF出力完了時
      if(errorUserList.length === 0){
        const actions: BtnProps[] = [
          {
            color: "indigo",
            children: "閉じる",
            onClick: () => {
              getUserIdCardCreatList();
              setIsOpenDialog(false);
              initDialog();
            }
          },
        ];
        setIsOpenDialog(true);
        setDialogTitle('PDF出力');
        setDialogChildren('　利用者IDカードの出力が完了しました。');
        setDialogActions(actions);
      }else{
        getUserIdCardCreatList();
        setPdfFlg(true);
        userPdfValidate(errorUserList);
      }
      setIsPdfCreate(false);
      setLoadProgress(-1);
    }
  }, [loadProgress])

  // 利用者IDカード情報の取得
  const getUserIdCardCreatList = async () => {
    const userIdCardCreatList = await systemParametersQuery.getUserIdCardCreatList(tenantId!);
    // 申請待ちは一覧に表示しない
    const filterList = userIdCardCreatList.filter((list: {status: string}) => list.status !== "waitRequest");
    let userIdCardCreatListResult: UserCardCreateState[] = [];
    filterList.map((item) => {
      userIdCardCreatListResult.push(userCardTimestampToDate(item));
    });
    setUserIdCardList(userIdCardCreatListResult);
    return userIdCardCreatListResult;
  }

  // ステータスの表示
  const statusDisp = (status: string) => {
    switch(status){
      case 'standBy':
        return "未出力";
      case 'inOperation':
        return "出力中";
      case 'error':
        return "エラー";
      case 'completion':
        return "出力完了";
      case 'impossible':
        return "出力不可";
      default:
        return "";
    }
  }

  const buttons = (item: UserCardCreateState) => {
    switch(item.status){
      case 'standBy':
        return (
          <div className="flex">
            <Button
              className="mr-2"
              onClick={async () => {
                if (await checkOption(tenantId, "userIdCard", dispatch)) {
                  // 選択したitemのステータスを更新
                  const userIdCardCreatList = await systemParametersQuery.getUserIdCardCreatList(tenantId!);
                  const latestItem = userIdCardCreatList.find((i: any) => i.id === item.id);
                  if (latestItem) {
                    const convertedItem = userCardTimestampToDate(latestItem);
                    setUserIdCardList(prevList => 
                      prevList.map(i => i.id === item.id ? convertedItem : i)
                    );
                    // 最新のステータスでチェック
                    if (!await canOutput(convertedItem, 'standBy')) {
                      openAlreadyRunningDialog(convertedItem);
                      return;
                    }
                    setPdfFlg(false);
                    pdfCreateConfirm(convertedItem, false);
                  }
                }
              }}
              children={"PDF出力"}/>
          </div>
        );
      case 'error':
        return (
          <div className="flex">
            <Button
              className="mr-2"
              onClick={async () => {
                if (await checkOption(tenantId, "userIdCard", dispatch)) {
                  // 選択したitemのステータスを更新
                  const userIdCardCreatList = await systemParametersQuery.getUserIdCardCreatList(tenantId!);
                  const latestItem = userIdCardCreatList.find((i: any) => i.id === item.id);
                  if (latestItem) {
                    const convertedItem = userCardTimestampToDate(latestItem);
                    setUserIdCardList(prevList => 
                      prevList.map(i => i.id === item.id ? convertedItem : i)
                    );
                    // 最新のステータスでチェック
                    if (!await canOutput(convertedItem, 'error')) {
                      openAlreadyRunningDialog(convertedItem);
                      return;
                    }
                    pdfCreateConfirm(convertedItem, true);
                  }
                }
              }}
              outline
              children={"再出力"}/>
          </div>
        );
      case 'completion':
        return (
          <div className="flex">
            {item.errorUserMaps && item.errorUserMaps.length > 0? (
              <>
                <Button
                  className="mr-2"
                  onClick={async () => {
                    if (await checkOption(tenantId, "userIdCard", dispatch)) {
                      setPdfFlg(false);
                      userPdfValidate([], item);
                    }
                  }}
                  color={"violet"}
                  outline
                  children={"詳細"}
                />
              </>
            ) : (
              <></>
            )}
          </div>
        );
      case 'impossible':
        return(
          <div className="flex">
            <Button
              className="mr-2"
              onClick={() => {
                setPdfFlg(false);
                userPdfValidate([], item);
              }}
              color={"violet"}
              outline
              children={"詳細"}
            />
          </div>
        );
      default:
        return (<></>);
    }
  };

  // 利用者IDカード出力画面の一覧表示
  const columns: GridCol[] = [
    {
      field: "No",
      headerName: "No.",
      style: "w-1/12",
    },
    {
      field: "excelFileName",
      headerName: "ファイル名",
      style: "w-4/12",
    },
    {
      field: "status",
      headerName: "ステータス",
      style: "w-1/12",
    },
    {
      field: "createCount",
      headerName: "カード数",
      style: "text-right",
    },
    {
      field: "requestAt",
      headerName: "申請日",
      style: "w-2/12",
    },
    {
      field: "createAt",
      headerName: "出力日",
      style: "w-2/12",
    },
    {
      field: "control",
      headerName: "",
      style: "w-2/12",
      sort: false
    },
  ];
  const sortList = () => {
    return userIdCardList.sort((a, b) => {
      const currentRowData = a.requestAt ?? new Date();
      const previewRowData = b.requestAt ?? new Date();
      if(currentRowData === previewRowData) {
        return 0;
      }
      return currentRowData < previewRowData  ? 1 : -1;
    });
  }
  const rowList: GridRow[] = sortList().map<GridRow>((item, index) => {
    return {
      rowData: {
        No: index + 1,
        excelFileName: item.pdfFileName ? item.pdfFileName : item.dispExcelFileName,
        status: statusDisp(item.status),
        createCount: item.createCount,
        requestAt: item.requestAt !== null? convertDateTimeToString(item.requestAt) : "",
        createAt: item.createAt !== null? convertDateTimeToString(item.createAt) : "",
        control: buttons(item),
      }
    }
  });

  // 詳細ボタン押下時の一覧表示
  const userIdCardErrorCol: GridCol[] = [
    {
      field: "userId",
      headerName: "利用者番号",
      style: ""
    },
    {
      field: "userName",
      headerName: "名前",
      style: "white-space: pre-line"
    },
    {
      field: "message",
      headerName: "理由",
      style: "white-space: pre-line"
    },
  ];
  const userIdCardErrorRow: GridRow[] = userIdCardErrorMessages.map<GridRow>((error) => {
    return {
      rowData: {
        userId: error.userId,
        userName: error.userName,
        message: (
          <>
            {error.message.map((item) => {
              return(
                <div style={{whiteSpace: "pre-line"}}>
                  {item}
              </div>
              )
            })}
          </>
        ),
      }
    }
  });
  
  // エラー詳細表示用の内容出力
  const userPdfValidate = (userData: User[], user?: UserCardCreateState) => {
    setUserIdCardErrorMessages([]);
    let errorMessages: any[] = [];
    if(user){
      if(user.errorUserMaps){
        user.errorUserMaps.map((item) => {
          errorMessages.push(item);
        })
      }
    }else{
      // PDF出力時のエラー表示作成
      userData.map((item) => {
        let messages: string[] = [];
        if(item.id !== ""){
          if(item.accountSuspensionFlg){
            messages.push(ConstMessage.ERR_USER_IDCARD_PDF_ACCOUNT);
          }
          if(!item.lastName || (item.lastName && item.lastName === "")){
            messages.push(ConstMessage.ERR_USER_IDCARD_PDF_LAST_NAME);
          }
          if(messages.length === 0){
            messages.push(ConstMessage.ERR_USER_IDCARD_PDF_UNDEFINED_ID);
          }
          errorMessages.push({
            'userId': item.displayId,
            'userName': item.lastName ?? "", 
            'message': messages
          });
        }else{
          messages.push(ConstMessage.ERR_USER_IDCARD_PDF_UNDEFINED_ID);
          errorMessages.push({
            'userId': item.displayId,
            'userName': "", 
            'message': messages
          });
        }
      });
    }
    if (errorMessages.length > 0) {
      setUserIdCardErrorMessages(errorMessages);
      toggleErrorInfoDialog();
    }
    return errorMessages;
  }

  const convertDateToStringCompactFormat = (date: Date) => {
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const HH = String(date.getHours()).padStart(2, "0");
    const MM = String(date.getMinutes()).padStart(2, "0");
    return `${yyyy}${mm}${dd}${HH}${MM}`;
  }

  // PDF出力
  const pdfCreateConfirm = (item: UserCardCreateState, reCreateFlg: boolean) => {
    // PDF出力前確認ダイアログ
    const actions: BtnProps[] = [
      {
        color: "indigo",
        outline: true,
        children: "キャンセル",
        className: "w-[132px]",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
        }
      },
      {
        color: "indigo",
        children: "OK",
        className: "w-[132px]",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
          pdfCreate(item);
        }
      },
    ];
    const dialogChildren = (<>
      <div className={"ml-3"}>
        {reCreateFlg? (
          <>利用者IDカードPDFを再出力します。</>
        ) : (
          <>
            以下のExcelファイルで利用者IDカードPDFを出力します。
            <ul className={`list-disc m-2 text-green-700`}>
              <li className={"flex"}><DocumentArrowUpIcon className={"h-6 w-6"}/>{item.dispExcelFileName}</li>
            </ul>
          </>
        )}
        <div className={'text-red-500 mb-1 mt-1'}>
          ※PDF出力は、利用者数によって<span className={'text-red-500 font-bold'}>作業時間が長時間になる場合があります。</span>
        </div>
        <div className={'text-red-500 mb-1'}>※一度実行すると中止することはできません。</div>
        <div className={'text-red-500 mb-5'}>※PDFの出力が完了次第メールでご連絡します。</div>
      </div>
    </>)

    setIsOpenDialog(true);
    setDialogTitle(reCreateFlg? "PDF再出力確認" : "PDF出力");
    setDialogChildren(dialogChildren);
    setDialogActions(actions);
  }
  // PDF出力処理
  const pdfCreate = async (item: UserCardCreateState) => {
    try{
      if(company){
        const list = await systemParametersQuery.getUserIdCardCreatList(tenantId!);
        const targetItem = list.find(i => i.id === item.id);
        if (targetItem && targetItem.status !== 'standBy' && targetItem.status !== 'error') {
          openAlreadyRunningDialog(item);
          return;
        }
        setIsPdfCreate(true);
        setLoadProgress(0);
        // ステータスを「出力中」に更新
        item.status = "inOperation";
        await systemParametersQuery.updateUserIdCardCreat(item);
        setUsersList([]);
        setErrorUserList([]);
        const filePath = ref(appStorage, `${item.excelFilePath}${item.excelFileName}`);
        const excelblob = await getBlob(filePath);
        const blobURL = window.URL.createObjectURL(excelblob as Blob);
        const promise = new Promise<any>(() => {
          const xhr = new XMLHttpRequest();
          xhr.onload = async () => {
            const result = xhr.response;
            const data = new Uint8Array(result);
            const workbook = new ExcelJS.Workbook();
            await workbook.xlsx.load(data as any);
            const worksheet = workbook.worksheets[0];
            worksheet.columns = [{ header: "利用者番号", key: "displayId" }];
            worksheet.pageSetup = {orientation:'portrait'};
            let usersList: any[] = [];
            let errorUsers: User[] = [];
            const total = worksheet.rowCount - 5;
            for (let i = 6; i < worksheet.rowCount + 1; i++) {
              const row = worksheet.getRow(i);
              const displayId = row.getCell("displayId").value as string;
              const userData = await userQuery.getUserData(company.id, company.displayId + displayId);
              if(userData && !userData?.accountSuspensionFlg && userData?.lastName && userData.lastName !== ""){
                // QR生成
                userData.qrData = await QRCode.toDataURL(`${company.displayId}${userData.displayId}`, { errorCorrectionLevel: 'H' });
                usersList.push(userData);
              }else{
                if(!userData){
                  errorUsers.push({
                    displayId: displayId,
                    id: "",
                    tenantId: "",
                    createAt: new Date(),
                    updateAt: new Date(),
                    accountSuspensionFlg: false
                  });
                }else{
                  errorUsers.push(userData);
                }
              }
              const progress = Math.round(((i - 5) / total) * 90);
              setLoadProgress(progress);
            };
            setErrorUserList(errorUsers);
            setUsersList(usersList);
            setLoadProgress(90);
            // ジョインのQRを生成
            const qr = await QRCode.toDataURL(machitappuUrl, { errorCorrectionLevel: 'H' });
            setLoadProgress(91);
            const matiTapData = {url: machitappuUrl, qr: qr};
            if(usersList.length > 0){
              setLoadProgress(92);
              const date = new Date();
              const yy = String(date.getFullYear()).slice(2);
              const mm = String(date.getMonth() + 1).padStart(2, "0");
              const pdfFilePath = `outPut/idCardPDF/${company.id}/${yy}${mm}/`;
              const pdfFileName = `利用者IDカード_${company.name}_${convertDateToStringCompactFormat(new Date())}.pdf`;
              const blob = await pdf(<UserIdCardPDF usersList={usersList} company={company} systemSetting={systemSetting} matiTapData= {matiTapData}/>).toBlob();
              setLoadProgress(96);
              // fireStrageに保存
              const storageRef = ref(appStorage, `${pdfFilePath}${pdfFileName}`);
              const newBlob = new Blob([blob]);
              await uploadBytes(storageRef, newBlob);
              setLoadProgress(98);

              // 利用者IDカードの更新
              updateData(item, errorUsers, usersList, pdfFilePath, pdfFileName);
              sendPDFCreateMail(item, usersList, errorUsers);
              setLoadProgress(99);
            }else{
              // ステータス更新
              updateData(item, errorUsers);
              sendPDFCreateMail(item, usersList, errorUsers);
              setLoadProgress(99);
            }
            setLoadProgress(100);
            setIsPdfCreate(false);
          };
          xhr.responseType = "arraybuffer";
          xhr.open("GET", blobURL);
          xhr.send(null);  
        });
        return promise;
      }
    }catch(err: any){
      console.error(err);
      setLoadProgress(-1);
      if (item) {
        item.status = 'error';
        await systemParametersQuery.updateUserIdCardCreat(item);
      }
      // ステータス更新
      if(err.name === "FirebaseError"){
        return dispatch(alertOpen(AlertValue(ConstMessage.ERR_USER_ID_CARD_NOTHING_EXCEL_FILE, "danger")));
      }else{
        return dispatch(alertOpen(AlertValue(ConstMessage.ERR_USER_ID_CARD_PDF_CREATE, "danger")));
      }
      
    }
  }

  // 選択したデータが出力可能か確認
  const canOutput = async (currentItem: UserCardCreateState, expectedStatus: string): Promise<boolean> => {
    const list = await systemParametersQuery.getUserIdCardCreatList(tenantId!);
    const targetItem = list.find(item => item.id === currentItem.id);
    return targetItem?.status === expectedStatus;
  };

  // 出力不可ダイアログ表示
  const openAlreadyRunningDialog = (currentItem: UserCardCreateState) => {
    const actions: BtnProps[] = [
      {
        color: "indigo",
        children: "閉じる",
        className: "w-[132px]",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
        }
      }
    ];

    setDialogTitle("PDF出力不可");
    setDialogChildren(
      <div className="ml-3 text-600">
        他のユーザーが処理しているため出力できません。
      </div>
    );
    setDialogActions(actions);
    setIsOpenDialog(true);
  };

  // 利用者IDカードの更新処理
  const updateData = async (item: UserCardCreateState, errorUserList: any = [], usersList: any = [], pdfFilePath: string = "", pdfFileName: string = "") => {
    if(usersList.length > 0){
      item.status = 'completion';
      item.createAt = new Date();
      item.pdfFilePath = pdfFilePath;
      item.pdfFileName = pdfFileName;
      item.createUserUid = currentUser?.uid;
    }else{
      // 全ての利用者が出力不可でPDFが作られなかった場合
      item.status = 'impossible';
    }
    if(errorUserList.length > 0){
      const errorUsers = userPdfValidate(errorUserList);
      item.errorUserMaps = errorUsers;
    }
    
    // 更新
    await systemParametersQuery.updateUserIdCardCreat(item);
  }

  // PDF出力メール送信処理
  const sendPDFCreateMail = async (item: UserCardCreateState, usersList: any = [], errorUserIds: any[]) => {
    try{
      let successFlg = false;
      // 申請者情報の取得
      const author = await authApi.requestGetStaffInfo(tenantId ?? "", item.requestUserUid);
      if (!author.success || author.data === undefined) {
        // APIエラー or 申請者情報無し
        throw new Error(author.data);
      }
      const requestAuth = author.data;

      // 団体宛
      const text = usersList.length > 0? completePDFMail(item, errorUserIds, requestAuth.displayName) : errorPDFMail(item, errorUserIds, requestAuth.displayName);
      const subject = `イベントサポートシステム 団体管理システム 利用者IDカード出力${usersList.length > 0 ? "完了" : "不可"}`;
      const toAddress = requestAuth.email;
      // メール送信
      const result = await authApi.requestSendEmailGeneral(text, subject, toAddress);
      console.log(result);
      if(result.success){
        successFlg = true;
      }
      if(!successFlg){
        throw new Error(ConstMessage.ERR_SEND_MAIL);
      }
    }catch(err: any){
        console.error(err)
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_SEND_MAIL, "danger")));
    }
  }

  // PDF出力完了用メール本文（団体宛）
  const completePDFMail = (item: UserCardCreateState, errorUserList: any[] ,toName: string | null) => {
    const lineBreak = "<br>";
    let errorListStr = "";
    if(errorUserList.length > 0){
      userPdfValidate(errorUserList).map((item) => {
        errorListStr = errorListStr + `　利用者番号：${item.userId}${lineBreak}　理由：${item.message}${lineBreak}`;
      })
    }
    const body = `${company?.name}　${toName} 様${lineBreak}以下申請書の利用者IDカードの出力が完了しました。${lineBreak}　申請書ファイル名：${lineBreak}　　${item.dispExcelFileName}${lineBreak}　申請日時：${lineBreak}　　${item.requestAt ? convertDateTimeToJapaneseString(item.requestAt) : ""}${lineBreak}利用者IDカードPDF${lineBreak}　PDFファイル名：${lineBreak}　　${item.pdfFileName}${lineBreak}　ファイル出力日時：${lineBreak}　　${item.createAt ? convertDateTimeToJapaneseString(item.createAt) : ""}${lineBreak}${lineBreak}${errorUserList.length > 0 ? `以下の利用者は利用者IDカードを出力できませんでした。${lineBreak}${errorListStr}`: ""}${lineBreak}管理画面からPDFファイルをダウンロード可能です。`;
    return body;
  }
  // 出力不可用メール本文（団体宛）
  const errorPDFMail = (item: UserCardCreateState, errorUserList: any = [], toName: string | null) => {
    let errorListStr = "";
    const lineBreak = "<br>";
    if(errorUserList.length > 0){
      userPdfValidate(errorUserList).map((item) => {
        errorListStr = errorListStr + `　利用者番号：${item.userId}${lineBreak}　理由：${item.message}${lineBreak}`;
      })
    }
    const body = `${company?.name}　${toName} 様${lineBreak}以下申請書の全利用者が出力不可のため、PDFの出力ができませんでした。${lineBreak}　申請書ファイル名：${lineBreak}　　${item.dispExcelFileName}${lineBreak}　申請日時：${lineBreak}　　${item.requestAt ? convertDateTimeToJapaneseString(item.requestAt) : ""}${lineBreak}${lineBreak}以下の利用者は利用者IDカードを出力できませんでした。${lineBreak}${errorListStr}`;
    return body;
  }

  // 汎用ダイアログ用
  // toggleDialog
  // initDialog
  const toggleDialog = () => {
    setIsOpenDialog(isOpenDialog);
  };
  const initDialog = () => {
    setDialogTitle("");
    setDialogChildren("");
    setDialogActions([]);
  };
  // 詳細ダイアログの開閉
  const toggleErrorInfoDialog = () => {
    setIsOpenErrorInfoDialog(!isOpenErrorInfoDialog);
  };

  // 利用者IDカードの権限有無を取得
  const displayStatus = async () => {
    const userIdCardAuth = await companiesApi.requestIdCardAuth(tenantId!);
    return userIdCardAuth.data;
  }
  
  return(
    <div className="h-full">
      {dispFlg ? (
        <>
          <Progress
            subText={progressSubText}
            open={isPdfCreate}
            progressNum={loadProgress}
            color={"green"}
          />
          <ModalAndAlmightyDialog
            toggle={toggleDialog}
            open={isOpenDialog}
            title={dialogTitle}
            children={dialogChildren}
            actions={dialogActions}
            height={"72"}
            closeOnClickOutside={false}
          />
          <UserIdCardErrorInfoModal
            open={isOpenErrorInfoDialog}
            toggle={toggleErrorInfoDialog}
            closeOnClickOutside={true}
            children={undefined}
            col={userIdCardErrorCol}
            row={userIdCardErrorRow}
            pdfFlg={pdfFlg}
            usersList={usersList}
          />
          <div className="flex items-center space-x-4 p-6 justify-between">
            <h2 className="flex text-xl font-semibold text-gray-900">利用者IDカード出力</h2>
          </div>
          {isLoad === true ? <Spiner /> :(
            <div className={"px-6"}>
              <TableList
                header
                sort={false}
                gridCol={columns}
                gridRow={rowList}
                limit={50}
              />
            </div>
          )}
        </>
      ) : (
        <>
          {isLoad === true ? <Spiner /> :(
            <>権限がないため表示できません</>
          )}
        </>
      )}
    </div>
  );
  }